<template>
  <b-row>

    <h2 class="pl-1">{{ $t('side_bar.shop') }}</h2>

    <!--  BEFORE TABLE  -->
    <div class="d-flex justify-content-between col-12">
      <b-col
        md="4"
        class="my-1 px-0"
      >
        <b-form-group
          class="mb-0"
        >
          <b-input-group size="md">
            <b-form-input
              id="filterInput"
              v-model="filter"
              type="search"
              :placeholder="$t('reusable.search_placeholder')"
            />
            <b-input-group-append>
              <b-button
                :disabled="!filter"
                @click="filter = ''"
              >
                {{ $t('reusable.clear_btn') }}
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>

      <div
        class="my-1 float-right"
      >
        <modal-button
          ripple="rgba(113, 102, 240, 0.15)"
          size="xl"
          name="modal-main"
          :modalButton="$t('reusable.modal_add_btn')"
          @btn-emit="createNewShop"
          @open-emit="clearData"
          :modal-title="$t('shops_table.add_shop')"
          variant="success"
        >
          <template v-slot:button @click="createNewShop">{{ $t('reusable.modal_add_btn') }}</template>

          <template v-slot:modal-body>
            <div class="d-flex flex-wrap">

              <!--   agent_id   -->
              <b-col cols="6">
                <infinity-scroll-select
                  class="mb-1"
                  v-model="agentId"
                  apiOption="filter.role.agent"
                  apiUrl="users.fetchUser"
                  :label="$t('shops_table.add_agent_name')"
                />
              </b-col>

              <!--   city_id   -->
              <b-col cols="6">
                <infinity-scroll-select
                  v-model="cityId"
                  class="mb-1"
                  apiUrl="regions.fetchCity"
                  :label="$t('shops_table.add_city_name')"
                />
              </b-col>

              <!--   company id   -->
              <b-col cols="6">
                <infinity-scroll-select
                  v-model="companyId"
                  class="mb-1"
                  apiUrl="shops.fetchCompany"
                  :label="$t('shops_table.add_company_name')"
                />
              </b-col>

              <!--   NAME   -->
              <b-col cols="6">
                <b-form-group
                  :label="$t('shops_table.add_shop_name')"
                  label-for="defaultLabel"
                >
                  <b-form-input
                    v-model="name"
                    id="defaultLabel"
                    size="lg"
                    :placeholder="$t('shops_table.add_shop_name')"
                  />
                </b-form-group>
              </b-col>

              <!--   ADDRESS   -->
              <b-col cols="6">
                <b-form-group
                  :label="$t('shops_table.add_shop_address')"
                  label-for="defaultLabel"
                >
                  <b-form-input
                    v-model="address"
                    id="defaultLabel"
                    size="lg"
                    :placeholder="$t('shops_table.add_shop_address')"
                  />
                </b-form-group>
              </b-col>

              <!--   number   -->
              <b-col cols="6">
                <b-form-group
                  :label="$t('shops_table.add_shop_number')"
                  label-for="defaultLabel"
                >
                  <b-form-input
                    v-model="number"
                    id="defaultLabel"
                    size="lg"
                    :placeholder="$t('shops_table.add_shop_number')"
                  />
                </b-form-group>
              </b-col>

              <!--   suppliers   -->
              <b-col cols="6">
                <infinity-scroll-select
                  :value="suppliers"
                  :multiple="true"
                  class="mb-1"
                  v-model="creatingSupplier.suppliers"
                  apiUrl="shops.fetchSupplier"
                  :label="$t('shops_table.add_supplier_name')"
                />
              </b-col>

              <!--   contacts   -->
              <b-col cols="6">
                <infinity-scroll-select
                  :value="contacts"
                  :multiple="true"
                  class="mb-1"
                  v-model="creatingContact.contacts"
                  apiUrl="shops.fetchContact"
                  :label="$t('shops_table.add_contact_name')"
                />
              </b-col>

              <!--   brands   -->
              <b-col cols="6">
                <infinity-scroll-select
                  :value="brands"
                  class="mb-1"
                  :multiple="true"
                  v-model="creatingBrand.brands"
                  apiUrl="products.fetchBrand"
                  :label="$t('shops_table.add_brand_name')"
                />
              </b-col>

              <!--   credit line   -->
              <b-col cols="6">

                <label>{{ $t('shops_table.shop_credit') }}</label>
                <div class="d-flex mt-1">
                  <b-form-checkbox
                    v-model="creditLine"
                    checked="true"
                    class="custom-control-success"
                    name="check-button"
                    switch
                  />
                  <span class="ml-1" style="color: grey" v-if="creditLine">{{ $t('reusable.credit_yes') }}</span>
                  <span class="ml-1" style="color: grey" v-else>{{ $t('reusable.credit_no') }}</span>
                </div>

              </b-col>

              <!--   location   -->
              <b-col cols="12">

                <template>
                  <yandex-map
                    style="width: 80%; height: 400px"
                    class="mt-4 mx-auto mb-5"
                    :settings="settings"
                    :coords="coords"
                    :zoom="10"
                    @click="mapOnClick"
                  >
                    <ymap-marker
                      :coords="coords"
                      marker-id="123"
                      hint-content="some hint"
                    />
                  </yandex-map>
                </template>

              </b-col>

            </div>
          </template>
        </modal-button>

      </div>
    </div>

    <!--  TABLE  -->
    <b-col cols="12">
      <b-table
        striped
        hover
        responsive
        sort-icon-left
        :busy="isBusy"
        :items="items"
        :fields="fields"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        :filter="filter"
        :filter-included-fields="filterOn"

        @filtered="onFiltered"
      >
        <template v-slot:head(is_deleted)="data">
          <b-form-select
            size="md"
            v-model="status"
            class="w-100"
          >
            <template #first>
              <option
                selected
                :value="null"
              >
                Выберите статус
              </option>
            </template>
            <option
              v-for="(status, index) in statuses"
              :key="index"
              :value="status"
            >
              {{ status }}
            </option>
          </b-form-select>
        </template>

        <template #cell(city.name)="data">
          {{ checkLocales(data.item.city.name) }}
        </template>

        <template #cell(city.region.name)="data">
          {{ checkLocales(data.item.city.region.name) }}
        </template>

        <template #cell(has_credit_line)="data">
          <span v-if="data.item.has_credit_line == false">{{ $t('reusable.credit_line_false') }}</span>
          <span v-else>{{ $t('reusable.credit_line_true') }}</span>
        </template>

        <template #cell(is_active)="data">
          <b-form-checkbox
            class="custom-control-success"
            name="check-button"
            v-model="data.item.is_active"
            @change="updateActive(data.item.id,$event)"
            switch
          >
            <span class="switch-icon-left">
              <feather-icon icon="CheckIcon"/>
            </span>
            <span class="switch-icon-right">
              <feather-icon icon="XIcon"/>
            </span>
          </b-form-checkbox>
        </template>

        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong class="ml-1">{{ $t('reusable.isBusy') }}</strong>
          </div>
        </template>

        <template #cell(crud_row)="data">
          <div class="d-flex float-right">

            <b-dropdown
              no-caret
              id="table-action-dropdown"
              variant="link"
              size="md"
            >
              <template
                #button-content
                class="p-0"
              >
                <feather-icon
                  icon="MoreVerticalIcon"
                  class="p-0"
                />
              </template>

              <!--   PREVIEW BUTTON   -->
              <b-dropdown-item
                class="px-0"
              >
                <modal-button
                  ripple="rgba(113, 102, 240, 0.15)"
                  size="xl"
                  :name="`modal-preview-${data.item.id}`"
                  :backdrop-close="true"
                  :hide-footer="true"
                  :modalButton="$t('region_table.country_edit_modal_title')"
                  :id="data.item.id"
                  :modal-title="$t('reusable.preview_btn')"
                  :disableButton="!editAllow"
                  variant="flat-primary"
                  @open-emit="getShopById(data.item.id)"
                  @btn-emit="updateShop(data.item.id, data.item)"
                >
                  <template
                    v-slot:button
                    style="width: 100% !important;"
                  >
                    <div class="d-flex align-items-center">
                      <feather-icon
                        icon="EyeIcon"
                        size="21"
                        style="margin-right: 6px"
                      />
                      <span>{{ $t('reusable.preview_btn') }}</span>
                    </div>
                  </template>

                  <template v-slot:modal-body>
                    <div class="d-flex flex-wrap">

                      <!--   agent_id   -->
                      <b-col cols="6">
                        <infinity-scroll-select
                          class="mb-1"
                          :disabled="!editAllow"
                          v-model="agentId"
                          apiOption="filter.role.agent"
                          apiUrl="users.fetchUser"
                          :label="$t('shops_table.add_agent_name')"
                        />
                      </b-col>

                      <!--   city_id   -->
                      <b-col cols="6">
                        <infinity-scroll-select
                          v-model="cityId"
                          class="mb-1"
                          :disabled="!editAllow"
                          apiUrl="regions.fetchCity"
                          :label="$t('shops_table.add_city_name')"
                        />
                      </b-col>

                      <!--   company id   -->
                      <b-col cols="6">
                        <infinity-scroll-select
                          v-model="companyId"
                          class="mb-1"
                          :disabled="!editAllow"
                          :multiple="true"
                          apiUrl="shops.fetchCompany"
                          :label="$t('shops_table.add_company_name')"
                        />
                      </b-col>

                      <!--   NAME   -->
                      <b-col cols="6">
                        <b-form-group
                          :label="$t('shops_table.add_shop_name')"
                          label-for="defaultLabel"
                        >
                          <b-form-input
                            v-model="name"
                            id="defaultLabel"
                            :disabled="!editAllow"
                            size="lg"
                            :placeholder="$t('shops_table.add_shop_name')"
                          />
                        </b-form-group>
                      </b-col>

                      <!--   ADDRESS   -->
                      <b-col cols="6">
                        <b-form-group
                          :label="$t('shops_table.add_shop_address')"
                          label-for="defaultLabel"
                        >
                          <b-form-input
                            v-model="address"
                            :disabled="!editAllow"
                            id="defaultLabel"
                            size="lg"
                            :placeholder="$t('shops_table.add_shop_address')"
                          />
                        </b-form-group>
                      </b-col>

                      <!--   number   -->
                      <b-col cols="6">
                        <b-form-group
                          :label="$t('shops_table.add_shop_number')"
                          label-for="defaultLabel"
                        >
                          <b-form-input
                            v-model="number"
                            id="defaultLabel"
                            :disabled="!editAllow"
                            size="lg"
                            :placeholder="$t('shops_table.add_shop_number')"
                          />
                        </b-form-group>
                      </b-col>

                      <!--   suppliers   -->
                      <b-col cols="6">
                        <infinity-scroll-select
                          :value="suppliers"
                          :multiple="true"
                          :disabled="!editAllow"
                          class="mb-1"
                          v-model="creatingSupplier.suppliers"
                          apiUrl="shops.fetchSupplier"
                          :label="$t('shops_table.add_supplier_name')"
                        />
                      </b-col>

                      <!--   contacts   -->
                      <b-col cols="6">
                        <infinity-scroll-select
                          :value="contacts"
                          :multiple="true"
                          :disabled="!editAllow"
                          class="mb-1"
                          v-model="creatingContact.contacts"
                          apiUrl="shops.fetchContact"
                          :label="$t('shops_table.add_contact_name')"
                        />
                      </b-col>

                      <!--   brands   -->
                      <b-col cols="4">
                        <infinity-scroll-select
                          :value="brands"
                          class="mb-1"
                          :disabled="!editAllow"
                          :multiple="true"
                          v-model="creatingBrand.brands"
                          apiUrl="products.fetchBrand"
                          :label="$t('shops_table.add_brand_name')"
                        />
                      </b-col>

                      <b-col cols="4">
                        <b-form-group
                          :label="$t('region_table.created_at')"
                          label-for="defaultLabel"
                        >
                          <b-form-input
                            v-model="createdAt"
                            id="defaultLabel"
                            size="lg"
                            :disabled="!editAllow"
                            :placeholder="$t('region_table.created_at')"
                          />
                        </b-form-group>
                      </b-col>

                      <b-col cols="4">
                        <b-form-group
                          :label="$t('region_table.updated_at')"
                          label-for="defaultLabel"
                        >
                          <b-form-input
                            v-model="updatedAt"
                            id="defaultLabel"
                            size="lg"
                            :disabled="!editAllow"
                            :placeholder="$t('region_table.updated_at')"
                          />
                        </b-form-group>
                      </b-col>

                      <!--   credit line   -->
                      <b-col cols="6">

                        <label>{{ $t('shops_table.shop_credit') }}</label>
                        <div class="d-flex">
                          <b-form-checkbox
                            v-model="creditLine"
                            :disabled="!editAllow"
                            checked="true"
                            class="custom-control-success"
                            name="check-button"
                            switch
                          />
                          <span class="ml-1" style="color: grey" v-if="creditLine">{{
                              $t('reusable.credit_yes')
                            }}</span>
                          <span class="ml-1" style="color: grey" v-else>{{ $t('reusable.credit_no') }}</span>
                        </div>

                      </b-col>

                      <!--   location   -->
                      <b-col cols="12">

                        <template>
                          <yandex-map
                            style="width: 80%; height: 400px"
                            class="mt-4 mx-auto mb-5"
                            :settings="settings"
                            :coords="coords"
                            :disabled="!editAllow"
                            :zoom="10"
                            @click="mapOnClick"
                          >
                            <ymap-marker
                              :coords="coords"
                              marker-id="123"
                              :disabled="!editAllow"
                              hint-content="some hint"
                            />
                          </yandex-map>
                        </template>

                      </b-col>

                    </div>

                  </template>


                </modal-button>
              </b-dropdown-item>

              <!--  EDIT BUTTON  -->
              <b-dropdown-item
                class="px-0"
              >
                <modal-button
                  ripple="rgba(113, 102, 240, 0.15)"
                  size="xl"
                  :name="`modal-edit-${data.item.id}`"
                  :modalButton="$t('reusable.save_btn')"
                  :id="data.item.id"
                  :modal-title="$t('reusable.modal_edit_btn')"
                  variant="flat-warning"
                  @open-emit="getShopById(data.item.id)"
                  @btn-emit="updateShop(data.item.id, data.item)"
                >
                  <template
                    v-slot:button
                    style="width: 100% !important;"
                  >
                    <div class="d-flex align-items-center">
                      <feather-icon
                        icon="Edit2Icon"
                        size="21"
                        style="margin-right: 6px"
                      />
                      <span>{{ $t('reusable.modal_edit_btn') }}</span>
                    </div>
                  </template>

                  <template v-slot:modal-body>
                    <div class="d-flex flex-wrap">

                      <!--   agent_id   -->
                      <b-col cols="6">
                        <infinity-scroll-select
                          class="mb-1"
                          v-model="agentId"
                          apiOption="filter.role.agent"
                          apiUrl="users.fetchUser"
                          :label="$t('shops_table.add_agent_name')"
                        />
                      </b-col>

                      <!--   city_id   -->
                      <b-col cols="6">
                        <infinity-scroll-select
                          v-model="cityId"
                          class="mb-1"
                          apiUrl="regions.fetchCity"
                          :label="$t('shops_table.add_city_name')"
                        />
                      </b-col>

                      <!--   company id   -->
                      <b-col cols="6">
                        <infinity-scroll-select
                          v-model="companyId"
                          class="mb-1"
                          :multiple="true"
                          apiUrl="shops.fetchCompany"
                          :label="$t('shops_table.add_company_name')"
                        />
                      </b-col>

                      <!--   NAME   -->
                      <b-col cols="6">
                        <b-form-group
                          :label="$t('shops_table.add_shop_name')"
                          label-for="defaultLabel"
                        >
                          <b-form-input
                            v-model="name"
                            id="defaultLabel"
                            size="lg"
                            :placeholder="$t('shops_table.add_shop_name')"
                          />
                        </b-form-group>
                      </b-col>

                      <!--   ADDRESS   -->
                      <b-col cols="6">
                        <b-form-group
                          :label="$t('shops_table.add_shop_address')"
                          label-for="defaultLabel"
                        >
                          <b-form-input
                            v-model="address"
                            id="defaultLabel"
                            size="lg"
                            :placeholder="$t('shops_table.add_shop_address')"
                          />
                        </b-form-group>
                      </b-col>

                      <!--   number   -->
                      <b-col cols="6">
                        <b-form-group
                          :label="$t('shops_table.add_shop_number')"
                          label-for="defaultLabel"
                        >
                          <b-form-input
                            v-model="number"
                            id="defaultLabel"
                            size="lg"
                            :placeholder="$t('shops_table.add_shop_number')"
                          />
                        </b-form-group>
                      </b-col>

                      <!--   suppliers   -->
                      <b-col cols="6">
                        <infinity-scroll-select
                          :value="suppliers"
                          :multiple="true"
                          class="mb-1"
                          v-model="creatingSupplier.suppliers"
                          apiUrl="shops.fetchSupplier"
                          :label="$t('shops_table.add_supplier_name')"
                        />
                      </b-col>

                      <!--   contacts   -->
                      <b-col cols="6">
                        <infinity-scroll-select
                          :value="contacts"
                          :multiple="true"
                          class="mb-1"
                          v-model="creatingContact.contacts"
                          apiUrl="shops.fetchContact"
                          :label="$t('shops_table.add_contact_name')"
                        />
                      </b-col>

                      <!--   brands   -->
                      <b-col cols="6">
                        <infinity-scroll-select
                          :value="brands"
                          class="mb-1"
                          :multiple="true"
                          v-model="creatingBrand.brands"
                          apiUrl="products.fetchBrand"
                          :label="$t('shops_table.add_brand_name')"
                        />
                      </b-col>

                      <!--   credit line   -->
                      <b-col cols="6">
                        <label>{{ $t('shops_table.shop_credit') }}</label>
                        <div class="d-flex mt-1">
                          <b-form-checkbox
                            v-model="creditLine"
                            checked="true"
                            class="custom-control-success"
                            name="check-button"
                            switch
                          />
                          <span class="ml-1" style="color: grey" v-if="creditLine">{{ $t('reusable.credit_yes') }}</span>
                          <span class="ml-1" style="color: grey" v-else>{{ $t('reusable.credit_no') }}</span>
                        </div>
                      </b-col>

                      <!--   location   -->
                      <b-col cols="12">

                        <template>
                          <yandex-map
                            style="width: 80%; height: 400px"
                            class="mt-4 mx-auto mb-5"
                            :settings="settings"
                            :coords="coords"
                            :zoom="10"
                            @click="mapOnClick"
                          >
                            <ymap-marker
                              :coords="coords"
                              marker-id="123"
                              hint-content="some hint"
                            />
                          </yandex-map>
                        </template>

                      </b-col>

                    </div>
                  </template>

                </modal-button>
              </b-dropdown-item>

              <!--  DELETE BUTTON  -->
              <b-dropdown-item>
                <modal-button
                  ripple="rgba(113, 102, 240, 0.15)"
                  size="sm"
                  :name="`modal-delete-${data.item.id}`"
                  :modalButton="$t('region_table.country_delete_modal_title')"
                  :modal-title="$t('region_table.country_delete_modal_title')"
                  variant="flat-danger"
                  :id="data.item.id"
                  @btn-emit="deleteShop"
                >
                  <template v-slot:button class="w-100">
                    <div class="d-flex align-items-center">
                      <feather-icon
                        icon="Trash2Icon"
                        size="21"
                        style="margin-right: 6px"
                      />
                      <span>{{ $t('reusable.modal_delete_btn') }}</span>
                    </div>
                  </template>

                  <template v-slot:modal-body>
                    <div class="my-2">
                      {{ $t('shops_table.delete_shop_text') }}
                    </div>
                  </template>

                </modal-button>
              </b-dropdown-item>

            </b-dropdown>

          </div>
        </template>
      </b-table>
    </b-col>

    <!--  PAGINATION  -->
    <b-col
      cols="12"
    >
      <b-pagination
        v-if="showPagination"
        v-model="pagination.current"
        :total-rows="pagination.total"
        :per-page="pagination.per_page"
        align="center"
        size="sm"
        class="my-0"
      />
    </b-col>
  </b-row>
</template>

<script>
import {
  BTable,
  BBadge,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BPagination,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BButton,
  BDropdown,
  BDropdownItem,
  BFormCheckbox,
  BSpinner,
  BCardText
} from 'bootstrap-vue'
import ModalButton from '@/views/ui/modals/ModalButton.vue'
import api from '@/services/api'
import InfinityScrollSelect from '@/views/ui/infinity-scroll/InfinityScrollSelect'
import { yandexMap, ymapMarker } from 'vue-yandex-maps'
import { credit } from '@core/utils/validations/validations'

export default {
  components: {
    BTable,
    BBadge,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BDropdown,
    BDropdownItem,
    BFormCheckbox,
    BSpinner,
    BCardText,
    ModalButton,
    InfinityScrollSelect,
    yandexMap,
    ymapMarker
  },
  data() {
    // const query = Object.assign({}, this.$route.query)

    return {
      settings: {
        apiKey: '',
        lang: 'ru_RU',
        coordorder: 'latlong',
        enterprise: false,
        version: '2.1',
        coords: []
      },

      coords: [
        41.30,
        69.27,
      ],
      createdAt: '',
      updatedAt: '',
      editAllow: false,
      isActive: false,
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
      filter: null,
      filterOn: [],
      status: null,
      infoModal: {
        id: 'info-modal',
        title: '',
        content: '',
      },
      statuses: [true, false],
      fields: [
        {
          key: 'id',
          label: 'ID',
        },
        {
          key: 'name',
          label: this.$t('shops_table.shop_name'),
          sortable: true,
        },
        {
          key: 'city.region.name',
          label: this.$t('shops_table.shop_address'),
          sortable: true,
        },
        {
          key: 'city.name',
          label: this.$t('shops_table.shop_address'),
          sortable: true,
        },
        {
          key: 'address',
          label: this.$t('shops_table.shop_address'),
          sortable: true,
        },
        {
          key: 'number',
          label: this.$t('shops_table.shop_number'),
          sortable: true,
        },
        {
          key: 'has_credit_line',
          label: this.$t('shops_table.shop_credit'),
          sortable: true,
        },
        {
          key: 'is_active',
          label: this.$t('shops_table.is_active'),
          sortable: true,
        },
        {
          key: 'created_at',
          label: this.$t('region_table.created_at'),
        },
        {
          key: 'updated_at',
          label: this.$t('region_table.updated_at'),
        },
        {
          key: 'crud_row',
          label: ' ',
        },
      ],
      variant: '',
      items: [],
      agentId: '',
      cityId: {},
      companyId: '',
      name: '',
      address: '',
      number: '',
      creditLine: false,
      location: [],
      suppliers: [],
      contacts: [],
      brands: [],
      creatingSupplier: {
        suppliers: []
      },
      creatingContact: {
        contacts: []
      },
      creatingBrand: {
        brands: []
      },
      isBusy: false,
      pagination: {
        current: 1,
        total: 1,
        per_page: 5
      }
    }
  },
  watch: {
    '$route.query': {
      handler(query) {
        this.pagination.current = query.page
        this.fetchShopList()
      },
      deep: true,
      immediate: true
    },
    'pagination.current': {
      handler(page) {
        this.replaceRouter({
          ...this.query,
          page,
        })
      },
    }
  },

  computed: {
    rows() {
      return this.items.length
    },

    query() {
      return Object.assign({}, this.$route.query)
    },

    hasItems() {
      return this.items.length > 0
    },

    showPagination() {
      return this.hasItems && !this.isBusy
    },

    sortOptions() {
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({
          text: f.label,
          value: f.key,
        }))
    },
  },

  methods: {

    updateActive(id, active) {
      api.shops.updateShopActive(id, active ? 1 : 0)
        .then(res => {
          console.log(res)
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(() => {

        })
    },

    mapOnClick(e) {
      this.coords = e.get('coords')
    },

    replaceRouter(query) {
      this.$router.replace({ query })
        .catch(() => {
        })
    },

    async fetchShopList() {
      const data = {
        with: {
          0: 'agent',
          1: 'city.region',
          2: 'company',
          3: 'suppliers',
          4: 'contacts',
          5: 'brands',
        },
        page: this.$route.query.page
      }
      this.isBusy = true
      await api.shops.fetchShop(data)
        .then(res => {
          this.items = res.data.data
          this.isActive = res.data.data.is_active
          this.pagination.per_page = res.data.meta.per_page
          this.pagination.total = res.data.meta.total
          this.pagination.current = res.data.meta.current_page
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(() => {
          this.isBusy = false
        })
    },

    // async getUserByRole() {
    //   const id = this.$route.params.id
    //   const data = {
    //     filter: {
    //       role: 'agent'
    //     }
    //   }
    //   this.editAllow = false
    //   await api.users.fetchUserByRole(id, data)
    //     .then(res => {
    //       console.log(res)
    //     })
    //     .catch((error) => {
    //       console.error(error)
    //     })
    //     .finally(() => {
    //
    //     })
    // },

    async getShopById(id) {
      const data = {
        with: {
          0: 'agent',
          1: 'city',
          2: 'company',
          3: 'suppliers',
          4: 'contacts',
          5: 'brands',
        },
      }
      this.editAllow = false
      await api.shops.fetchOneShop(id, data)
        .then(res => {
          this.createdAt = res.data.created_at
          this.updatedAt = res.data.updated_at
          this.agentId = res.data.agent
          this.cityId = res.data.city
          this.companyId = res.data.company
          this.name = res.data.name
          this.address = res.data.address
          this.number = res.data.number
          this.creatingSupplier.suppliers = res.data.suppliers
          this.creatingContact.contacts = res.data.contacts
          this.creatingBrand.brands = res.data.brands
          this.creditLine = res.data.has_credit_line
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(() => {

        })
    },

    clearData() {
      this.agentId = null,
        this.cityId = null
      this.companyId = null
      this.name = ''
      this.address = ''
      this.number = ''
      this.creatingContact.contacts = null
      this.creatingSupplier.suppliers = null
      this.creatingBrand.brands = null
      this.creditLine = false
      this.coords = [
        41.30,
        69.27,
      ]
    },

    createNewShop() {
      const data = {
        agent_id: this.agentId.id,
        city_id: this.cityId.id,
        company_id: this.companyId.id,
        name: this.name,
        address: this.address,
        has_credit_line: this.creditLine ? 1 : 0,
        number: this.number,
        suppliers: this.creatingSupplier.suppliers.map(item => item.id),
        contacts: this.creatingContact.contacts.map(item => item.id),
        brands: this.creatingBrand.brands.map(item => item.id),
        location: this.coords
      }
      // this.getUserByRole()
      api.shops.createShop(data)
        .then(res => {
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(() => {
          this.fetchShopList()
          this.name = ''
        })
    },

    deleteShop(id) {
      api.shops.deleteShop(id)
        .then(res => {
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(() => {
          this.fetchShopList()
        })
    },

    updateShop(id, data) {
      const body = {
        agent_id: this.agentId.id,
        city_id: this.cityId.id,
        company_id: this.companyId.id,
        name: this.name,
        address: this.address,
        has_credit_line: this.creditLine ? 1 : 0,
        number: this.number,
        suppliers: this.creatingSupplier.suppliers.map(item => item.id),
        contacts: this.creatingContact.contacts.map(item => item.id),
        brands: this.creatingBrand.brands.map(item => item.id),
        location: this.coords
      }
      api.shops.updateShop(id, body)
        .then(res => {
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(() => {
          this.fetchShopList()
        })
    },

    changeVariant(value) {
      if (value.is_active) return 'success'
      return 'danger'
    },

    checkLocales(value) {
      if (localStorage.lang === 'ru') {
        return value.ru
      }
      return value.uz
    },

    info(item, index, button) {
      this.infoModal.title = `Row index: ${index}`
      this.infoModal.content = JSON.stringify(item, null, 2)
      this.$root.$emit('bv::show::modal', this.infoModal.id, button)
    },

    resetInfoModal() {
      this.infoModal.title = ''
      this.infoModal.content = ''
    },

    onFiltered(filteredItems) {
      // Trigger pagination to update the number of modals/pages due to filtering
      this.totalRows = filteredItems.length
      this.currentPage = 1
    },
  },
}
</script>

<style lang="scss">

[dir] .dropdown-item {
  padding: 0;
}


</style>
